import { createEnv } from '@t3-oss/env-nextjs'
import { vercel } from '@t3-oss/env-nextjs/presets-zod'
import { z } from 'zod'

import { hasWindow } from '@/utils/has-window'

// ... existing code ...
export function checkIsServer() {
  // 1. Check if it's a Client (Browser Main Thread)
  if (hasWindow()) {
    return false
  }

  // 2. Check if it's a Service Worker
  const isServiceWorker =
    typeof self !== 'undefined' &&
    ((typeof ServiceWorkerGlobalScope !== 'undefined' &&
      self instanceof ServiceWorkerGlobalScope) ||
      typeof (self as unknown as ServiceWorkerGlobalScope).clients !==
        'undefined')

  if (isServiceWorker) {
    return false
  }

  // 3. Server environment
  // Uncomment for stricter Node.js checking:
  // return typeof process !== 'undefined' && !!process.versions?.node;
  return true
}

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    // Database
    DATABASE_URL: z.string().url(),
    // Supabase
    SUPABASE_SERVICE_ROLE_KEY: z.string(),
    // MailerLite
    MAILERLITE_API_KEY: z.string(),
    MAILERLITE_MOMENTS_GROUP_ID: z.string(),
    // Gumroad
    GUMROAD_WEBHOOK_URL: z.string().url(),
    GUMROAD_ACCESS_TOKEN: z.string(),
    GUMROAD_PRODUCT_PERMALINK: z.string(),
    // AI
    OPENAI_API_KEY: z.string(),
    ANTHROPIC_API_KEY: z.string().optional(),
    DEEPSEEK_API_KEY: z.string().optional(),
    GROQ_API_KEY: z.string().optional(),
    // Flags
    INSIGHT_ENABLED: z.enum(['true', 'false']).default('false'),
    PAYMENTS_ENABLED: z.enum(['true', 'false']).default('false'),
    OTP_SIGN_IN_ENABLED: z.enum(['true', 'false']).default('false'),
    // App config
    STREAK_RESET_DAYS: z
      .string()
      .default('2')
      .transform((val) => {
        const num = Number(val)
        return isNaN(num) ? 2 : num // Default to 2 if not a valid number
      }),
    // Auth
    BETTER_AUTH_SECRET: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    GOOGLE_CLIENT_ID: z.string(),
    RESEND_API_KEY: z.string(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    // Vercel
    NEXT_PUBLIC_VERCEL_ENV: z
      .enum(['preview', 'development', 'production'])
      .optional(),
    NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL: z.string().optional(),
    // Supabase
    NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string(),
    // OneSignal
    NEXT_PUBLIC_ONESIGNAL_APP_ID: z.string(),
    // Gumroad
    NEXT_PUBLIC_GUMROAD_PRODUCT_URL: z.string().url(),
    NEXT_PUBLIC_GUMROAD_SUCCESS_URL: z.string().url(),
    NEXT_PUBLIC_RESEND_FROM_EMAIL: z.string(),
  },

  shared: {
    NODE_ENV: z
      .enum(['development', 'test', 'production'])
      .default('development'),
    VERSION: z.string(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  experimental__runtimeEnv: {
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_RESEND_FROM_EMAIL: process.env.NEXT_PUBLIC_RESEND_FROM_EMAIL,
    NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL:
      process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_ONESIGNAL_APP_ID: process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID,
    NEXT_PUBLIC_GUMROAD_PRODUCT_URL:
      process.env.NEXT_PUBLIC_GUMROAD_PRODUCT_URL,
    NEXT_PUBLIC_GUMROAD_SUCCESS_URL:
      process.env.NEXT_PUBLIC_GUMROAD_SUCCESS_URL,
    VERSION: process.env.VERSION,
    NODE_ENV: process.env.NODE_ENV,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
  extends: [vercel()],
  isServer: checkIsServer(),
})
